import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@mantine/core";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";
import apiService from "../../services/api";

interface PaymentVerificationData {
  Code?: number;
  Message?: string;
  payment_status?: string;
  amount?: number;
  transaction_id?: string;
  error_details?: string;
  detail?: string;
  [key: string]: unknown;
}

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentVerificationData | null>(null);

  useEffect(() => {
    const processPayment = async () => {
      const sessionId = searchParams.get("session_id");
      const requestId = searchParams.get("request_id");
      const trainerId = searchParams.get("trainer_id");
      const paymentSuccess = searchParams.get("payment") === "success";

      // Check if this is a trainer registration payment
      if (trainerId && paymentSuccess) {
        try {
          console.log("Verifying trainer registration payment for trainer_id:", trainerId);
          const verifyResponse = (await apiService.verifyTrainerRegistrationPayment({
            trainer_id: trainerId,
          })) as PaymentVerificationData;

          console.log("Trainer payment verification response:", verifyResponse);

          if (verifyResponse.Code === 200 && verifyResponse.payment_status === "completed") {
            setPaymentData(verifyResponse);
            setLoading(false);

            // Redirect to trainer portal after successful payment
            setTimeout(() => {
              navigate("/trainer");
            }, 3000);
          } else {
            setError(
              verifyResponse.Message || "Payment verification failed. Please contact support.",
            );
            setLoading(false);
          }
        } catch (err: unknown) {
          console.error("Error verifying trainer payment:", err);
          setError("Failed to verify payment: " + ((err as Error).message || String(err)));
          setLoading(false);
        }
        return;
      }

      // Handle session payment (client session payment)
      if (!sessionId) {
        setError("No payment session ID found");
        setLoading(false);
        return;
      }

      if (!requestId) {
        setError(
          "Missing session request information. Please contact support if the payment was processed.",
        );
        setLoading(false);
        return;
      }

      try {
        // Verify payment
        console.log("Verifying payment with session_id:", sessionId, "request_id:", requestId);
        const verifyResponse = (await apiService.verifyCheckoutSession({
          session_id: sessionId,
          request_id: requestId,
        })) as PaymentVerificationData;

        console.log("Payment verification response:", verifyResponse);

        if (verifyResponse.Code === 200) {
          // Check if payment_status is 'completed' or if the response indicates success
          if (
            verifyResponse.payment_status === "completed" ||
            verifyResponse.Message === "Payment verified"
          ) {
            setPaymentData(verifyResponse);
            setLoading(false);

            setTimeout(() => {
              navigate("/client-portal");
            }, 3000);
          } else {
            // Payment might still be processing - show a message but don't treat as error
            console.warn(
              "Payment verification returned status:",
              verifyResponse.payment_status,
              "Message:",
              verifyResponse.Message,
            );
            setError(
              `Payment status: ${verifyResponse.payment_status || "unknown"}. ${verifyResponse.Message || "Payment may still be processing. Please check your session requests page."}`,
            );
            setLoading(false);
          }
        } else {
          const errorMsg =
            verifyResponse.Message ||
            verifyResponse.error_details ||
            verifyResponse.detail ||
            "Unknown error";
          console.error("Payment verification failed:", verifyResponse);
          setError("Payment verification failed: " + errorMsg);
          setLoading(false);
        }
      } catch (err: unknown) {
        console.error("Error processing payment:", err);
        setError("Failed to process payment: " + ((err as Error).message || String(err)));
      } finally {
        setLoading(false);
      }
    };

    processPayment();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
        <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 max-w-[500px] w-full text-center shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border text-foreground">
          <div className="text-lg text-muted-foreground">Processing payment...</div>
        </div>
      </div>
    );
  }

  if (error) {
    const trainerId = searchParams.get("trainer_id");
    const isTrainerPayment = trainerId && searchParams.get("payment") === "success";
    const redirectPath = isTrainerPayment ? "/trainer" : "/client-portal";
    const redirectText = isTrainerPayment ? "Go to Trainer Portal" : "Go to Client Portal";
    const helpMessage = isTrainerPayment
      ? "If you completed payment, please contact support if you continue to see this error."
      : "If you completed payment, please check your session requests page. The payment may still be processing.";

    return (
      <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
        <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 max-w-[500px] w-full text-center shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border text-foreground">
          <IconAlertTriangle size={64} className="mx-auto mb-5 text-destructive" stroke={1.5} />
          <h1 className="text-xl sm:text-2xl font-bold mb-4 text-destructive">Payment Status</h1>
          <p className="text-destructive mt-5">{error}</p>
          <p className="mt-4 text-sm text-muted-foreground">{helpMessage}</p>
          <Button onClick={() => navigate(redirectPath)} radius="md" size="md" className="mt-5">
            {redirectText}
          </Button>
        </div>
      </div>
    );
  }

  // Determine redirect message based on payment type
  const trainerId = searchParams.get("trainer_id");
  const isTrainerPayment = trainerId && searchParams.get("payment") === "success";
  const redirectMessage = isTrainerPayment
    ? "Redirecting to trainer portal..."
    : "Redirecting to client portal...";
  const successMessage = isTrainerPayment
    ? "Your registration payment has been processed successfully. Welcome to Pheefee!"
    : "Your payment has been processed successfully. You can now join the session once the trainer is ready.";

  return (
    <div className="font-sans bg-background min-h-screen flex items-center justify-center p-5">
      <div className="bg-card rounded-2xl p-5 sm:p-8 md:p-10 max-w-[500px] w-full text-center shadow-[0_20px_60px_rgba(230,126,34,0.15)] border border-border text-foreground">
        <IconCircleCheck size={64} className="mx-auto mb-5 text-primary" stroke={1.5} />
        <h1 className="text-xl sm:text-2xl font-bold mb-4 text-success">Payment Successful!</h1>
        <p className="text-lg mb-8 text-muted-foreground">{successMessage}</p>
        {paymentData && (
          <div className="mt-5 p-4 bg-muted rounded-lg border border-border">
            <p className="my-1 text-sm">
              <strong>Amount:</strong> $
              {paymentData.amount?.toFixed(2) || (isTrainerPayment ? "50.00" : "0.00")}
            </p>
            {paymentData.transaction_id && (
              <p className="my-1 text-sm">
                <strong>Transaction ID:</strong> {paymentData.transaction_id?.substring(0, 20)}...
              </p>
            )}
          </div>
        )}
        <p className="mt-5 text-sm text-muted-foreground">{redirectMessage}</p>
      </div>
    </div>
  );
};

export default PaymentSuccess;
